/**
 * PostHog analytics integration — lazy-loaded.
 *
 * PostHog is privacy-friendly (self-hostable, GDPR-compliant, cookie-free mode).
 * The SDK is dynamically imported to avoid adding ~180KB to the initial bundle.
 *
 * Set VITE_PUBLIC_POSTHOG_KEY in your .env to enable analytics.
 * If the key is missing, all tracking calls become no-ops.
 */

type PostHogAPI = {
  init: (key: string, options: Record<string, unknown>) => void;
  identify: (id: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
};

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com";

let initialised = false;
let ph: PostHogAPI | null = null;
let initPromise: Promise<PostHogAPI | null> | null = null;

/**
 * Lazily load and initialise PostHog. Returns null if no key configured.
 */
async function getPostHog(): Promise<PostHogAPI | null> {
  if (!POSTHOG_KEY) return null;
  if (ph) return ph;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // posthog-js ESM default export has .init, .identify, .capture, .reset
      const mod = await import("posthog-js");
      const api = (mod.default ?? mod.posthog ?? mod) as PostHogAPI;

      if (!initialised) {
        api.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          autocapture: true,
          capture_pageview: true,
          capture_pageleave: true,
          persistence: "localStorage",
          disable_session_recording: true,
          mask_all_text: false,
          mask_all_element_attributes: false,
          respect_dnt: true,
          advanced_disable_decide: true,
        });
        initialised = true;
      }

      ph = api;
      return api;
    } catch {
      // PostHog CDN/load failure — silently degrade
      return null;
    }
  })();

  return initPromise;
}

/**
 * Initialise PostHog. Call once at app mount.
 * Kicks off the lazy load — events will fire once loaded.
 */
export function initAnalytics(): void {
  if (!POSTHOG_KEY) return;
  void getPostHog();
}

/**
 * Identify a user. Call after successful login/signup.
 */
export function identifyUser(userId: string, properties?: Record<string, string | number | boolean>): void {
  void getPostHog().then((api) => {
    if (!api) return;
    api.identify(userId, properties as Record<string, unknown>);
  });
}

/**
 * Reset identity. Call on sign-out.
 */
export function resetIdentity(): void {
  void getPostHog().then((api) => {
    if (!api) return;
    api.reset();
  });
}

// ─── Conversion events ──────────────────────────────────────────────────────

function capture(event: string, properties?: Record<string, unknown>): void {
  void getPostHog().then((api) => {
    if (!api) return;
    api.capture(event, properties);
  });
}

/** Track when a user views the signup page. */
export function trackSignupPageViewed(): void { capture("signup_page_viewed"); }

/** Track when a user completes signup. */
export function trackSignupCompleted(properties?: { plan?: string }): void { capture("signup_completed", properties); }

/** Track when a user views the login page. */
export function trackLoginPageViewed(): void { capture("login_page_viewed"); }

/** Track when a user views the pricing page. */
export function trackPricingPageViewed(): void { capture("pricing_page_viewed"); }

/** Track when a user clicks a pricing CTA. */
export function trackPricingCtaClicked(properties: { plan: string; location: string }): void { capture("pricing_cta_clicked", properties); }

/** Track when a user views the builder (authenticated). */
export function trackBuilderViewed(): void { capture("builder_viewed"); }

/** Track when AI generation starts. */
export function trackAiGenerationStarted(): void { capture("ai_generation_started"); }

/** Track when AI generation succeeds. */
export function trackAiGenerationCompleted(properties: { promptLength: number }): void { capture("ai_generation_completed", properties); }

/** Track when AI generation fails (rate limit or error). */
export function trackAiGenerationFailed(properties: { reason: "rate_limit" | "error" }): void { capture("ai_generation_failed", properties); }

/** Track when a user exports a template. */
export function trackExportCompleted(properties: { format: "html" | "react"; source: "template_detail" | "builder"; brand?: string; slug?: string }): void { capture("export_completed", properties); }

/** Track when a user views a template detail page. */
export function trackTemplateDetailViewed(properties: { brand: string; slug: string; category: string }): void { capture("template_detail_viewed", properties); }

/** Track when a user clicks "Edit with AI" on a template detail page. */
export function trackEditWithAiClicked(properties: { brand: string; slug: string }): void { capture("edit_with_ai_clicked", properties); }

/** Track when a user clicks the signup CTA from the builder preview gate. */
export function trackBuilderGateCtaClicked(properties: { cta: "signup" | "login" | "signup-dismissed" | "login-dismissed" }): void { capture("builder_gate_cta_clicked", properties); }

/** Track when a draft is saved. */
export function trackDraftSaved(): void { capture("draft_saved"); }

/** Track when a user starts checkout (clicked upgrade). */
export function trackCheckoutStarted(properties: { plan: string }): void { capture("checkout_started", properties); }

/** Track newsletter subscription from footer. */
export function trackNewsletterSubscribed(properties: { source: string }): void { capture("newsletter_subscribed", properties); }

/** Track forgot password page viewed. */
export function trackForgotPasswordViewed(): void { capture("forgot_password_viewed"); }

/** Track password reset requested. */
export function trackPasswordResetRequested(): void { capture("password_reset_requested"); }

/** Track password reset completed. */
export function trackPasswordResetCompleted(): void { capture("password_reset_completed"); }

/** Track when a user submits post-export feedback. */
export function trackExportFeedbackSubmitted(properties: { format: string; source: string; response: string }): void { capture("export_feedback_submitted", properties); }

/** Track when a user dismisses the post-export feedback prompt. */
export function trackExportFeedbackDismissed(properties: { format: string; source: string }): void { capture("export_feedback_dismissed", properties); }

/** Generic event tracking for custom events. */
export function trackEvent(event: string, properties?: Record<string, unknown>): void { capture(event, properties); }